import { FC, useCallback, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@src/lib/ui/Dialog";
import { Button } from "@src/lib/ui/Button";
import UserTypeSelection from "@src/features/users/dialogs/inviteOrEditUserDialog/UserTypeSelection";
import useInviteUserMutationWrapper from "@src/features/users/dialogs/inviteOrEditUserDialog/hooks/useInviteUserMutationWrapper";
import useUserState from "@src/features/users/dialogs/inviteOrEditUserDialog/hooks/useUserState";
import GroupMultiSelectInput from "@src/features/users/ui/GroupMultiSelectInput";
import PhoneNumberInput from "@src/lib/ui/PhoneNumberInput";
import Text from "@src/lib/ui/Text";
import { Input } from "@src/lib/ui/Input";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import useIsFullAdmin from "@src/features/app/hooks/useIsFullAdmin";
import useOrderedLanguageChoices from "@src/features/contentDetail/hooks/useOrderedLanguageChoices";
import {
  GroupType,
  LocationGroupType,
  UserInput,
  UserType,
} from "@src/types.generated";
import useHiddenLocationGroupTypes from "@src/features/home/hooks/useHiddenLocationGroupTypes";
import LocationGroupMultiSelectField from "@src/features/locations/ui/LocationGroupMultiSelectField";
import { locationGroupTypeDisplayNamePlural } from "@src/features/home/constants";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import isEmail from "validator/lib/isEmail";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@src/lib/ui/Command";
import ChevronDownIcon from "@src/lib/icons/18px/chevron-down";
import { gql } from "@apollo/client";

type Props = {
  initialLocationId?: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type ValidationErrors = {
  firstName: boolean;
  lastName: boolean;
  phoneNumber: boolean;
  email: boolean;
  locations: boolean;
};

const InviteAdminOrManagerDialog: FC<Props> = ({
  initialLocationId,
  open,
  onOpenChange,
}) => {
  const { userActionPermissions } = useActionPermissions();
  const {
    firstName,
    lastName,
    phoneNumber,
    email,
    jobTitle,
    language,
    userType,
    roleIds,
    locationIds,
    setFirstName,
    setLastName,
    setPhoneNumber,
    setEmail,
    setJobTitle,
    setLanguage,
    setUserType,
    setRoleIds,
    setLocationIds,
  } = useUserState(undefined, initialLocationId ? [initialLocationId] : []);

  const [errors, setErrors] = useState<ValidationErrors>({
    firstName: false,
    lastName: false,
    phoneNumber: false,
    email: false,
    locations: false,
  });
  const isFullAdmin = useIsFullAdmin();
  const hiddenLocationGroupTypes = useHiddenLocationGroupTypes();
  const [locationGroupIdsByType, setLocationGroupIdsByType] = useState<
    Record<LocationGroupType, string[]>
  >({
    [LocationGroupType.Region]: [],
    [LocationGroupType.Franchise]: [],
    [LocationGroupType.Brand]: [],
  });
  // Region / Brand / Franchise assignments may only be set by admins —
  // backend (`CreateUserResolver` / `UpdateUserResolver`) raises if a manager
  // tries to set `locationGroupIds`. Hide the pickers when the inviter isn't
  // a full admin so the form matches what the backend will accept.
  const showLocationGroups = userType === UserType.Manager && isFullAdmin;
  const allLocationGroupIds = useMemo(
    () => [
      ...locationGroupIdsByType[LocationGroupType.Region],
      ...locationGroupIdsByType[LocationGroupType.Franchise],
      ...locationGroupIdsByType[LocationGroupType.Brand],
    ],
    [locationGroupIdsByType],
  );
  const getValidationErrors = useCallback(() => {
    return {
      firstName: firstName.length === 0,
      lastName: lastName.length === 0,
      phoneNumber: phoneNumber
        ? !isPossiblePhoneNumber(phoneNumber || "") ||
          phoneNumber.startsWith("+1555")
        : false,
      email: !isEmail(email),
      locations: locationIds.length === 0 && userType !== UserType.Admin,
    };
  }, [email, firstName, lastName, phoneNumber, locationIds, userType]);
  const getHasValidationErrors = useCallback(
    (errors: ValidationErrors) => {
      if (userType === UserType.Employee) {
        return errors.phoneNumber || errors.firstName || errors.lastName;
      } else {
        return (
          errors.email ||
          errors.phoneNumber ||
          errors.firstName ||
          errors.lastName ||
          errors.locations
        );
      }
    },
    [userType],
  );
  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange]);
  const [createUserMutation, { loading: createUserLoading }] =
    useInviteUserMutationWrapper(handleClose);
  const userInput: UserInput = {
    firstName,
    lastName,
    userType: userType,
    phoneNumber: phoneNumber.length > 0 ? phoneNumber : null,
    language: language,
    email: email || null,
    jobTitle: jobTitle || null,
    roleIds: roleIds,
    locationIds: locationIds,
    ...(showLocationGroups && {
      locationGroupIds: { value: allLocationGroupIds },
    }),
  };
  const langChoices = useOrderedLanguageChoices();
  const [langPopoverOpen, setLangPopoverOpen] = useState(false);
  const selectedLangLabel = useMemo(() => {
    return langChoices.find((c) => c.value === language)?.label;
  }, [langChoices, language]);
  const locationString = useMemo(() => {
    let ret =
      userType === UserType.Employee
        ? "Where do they work?"
        : "Which locations do they manage?";

    if (userType != UserType.Admin) {
      ret = ret + "*";
    }
    return ret;
  }, [userType]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-w-[600px] flex-col gap-0 p-0 sm:max-h-[90vh]">
        <DialogHeader className="border-b border-input p-4">
          <DialogTitle>Invite user</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 overflow-auto p-4">
          {isFullAdmin && (
            <UserTypeSelection
              userType={userType}
              setUserType={setUserType}
              disableTraineeOption
              showDescription
            />
          )}
          <div className="grid grid-cols-2 gap-4">
            <Input
              value={firstName}
              label="First Name*"
              onChange={(e) => {
                setErrors({ ...errors, firstName: false });
                setFirstName(e.target.value);
              }}
              aria-invalid={errors.firstName}
            />
            <Input
              value={lastName}
              label="Last Name*"
              onChange={(e) => {
                setErrors({ ...errors, lastName: false });
                setLastName(e.target.value);
              }}
              aria-invalid={errors.lastName}
            />
          </div>
          <PhoneNumberInput
            label="Mobile Phone Number (Highly recommended)"
            value={phoneNumber}
            onChange={(val) => {
              setErrors({ ...errors, phoneNumber: false });
              setPhoneNumber(val || "");
            }}
            error={errors.phoneNumber}
          />
          <Input
            value={email}
            label="Email Address*"
            onChange={(e) => {
              setErrors({ ...errors, email: false });
              setEmail(e.target.value);
            }}
            aria-invalid={errors.email}
          />
          <Input
            value={jobTitle}
            label="Job Title"
            onChange={(e) => {
              setJobTitle(e.target.value);
            }}
          />
          {langChoices.length !== 0 && (
            <div className="flex flex-col gap-1">
              <Text variant="fine-print-title">Language</Text>
              <Popover open={langPopoverOpen} onOpenChange={setLangPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={langPopoverOpen}
                    className="flex w-full justify-between px-3"
                  >
                    <span className="truncate">
                      {selectedLangLabel || "English"}
                    </span>
                    <ChevronDownIcon className="ml-2 size-4 shrink-0 text-gray-5" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                  disablePortal
                >
                  <Command>
                    <CommandInput placeholder="Search..." className="h-9" />
                    <CommandList>
                      <CommandEmpty>No results found.</CommandEmpty>
                      <CommandGroup>
                        {langChoices.map((option) => (
                          <CommandItem
                            key={option.value}
                            value={option.label}
                            onSelect={() => {
                              setLanguage(option.value as Language);
                              setLangPopoverOpen(false);
                            }}
                          >
                            <Text variant="compact-description">
                              {option.label}
                            </Text>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}
          <div>
            <Text variant="fine-print-title" className="mb-1">
              {locationString}
            </Text>
            <GroupMultiSelectInput
              selectedGroupIds={locationIds}
              setSelectedGroupIds={(newIds) => {
                setErrors({ ...errors, locations: false });
                setLocationIds(newIds);
              }}
              type={GroupType.Location}
              userType={userType}
              error={errors.locations}
            />
          </div>
          {showLocationGroups &&
            [
              LocationGroupType.Region,
              LocationGroupType.Franchise,
              LocationGroupType.Brand,
            ]
              .filter((type) => !hiddenLocationGroupTypes.includes(type))
              .map((type) => (
                <LocationGroupMultiSelectField
                  key={type}
                  type={type}
                  label={`Which ${locationGroupTypeDisplayNamePlural[
                    type
                  ].toLowerCase()} do they manage?`}
                  locationGroupIds={locationGroupIdsByType[type]}
                  setLocationGroupIds={(ids) =>
                    setLocationGroupIdsByType((prev) => ({
                      ...prev,
                      [type]: ids,
                    }))
                  }
                />
              ))}
          <div>
            <Text variant="fine-print-title" className="mb-1">
              What is their role?
            </Text>
            <GroupMultiSelectInput
              selectedGroupIds={roleIds}
              setSelectedGroupIds={setRoleIds}
              type={GroupType.Role}
              userType={userType}
            />
          </div>
        </div>
        <DialogFooter className="border-t border-input p-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            dataTestId="invite-user-button"
            disabled={createUserLoading}
            onClick={() => {
              const validationErrors = getValidationErrors();
              const hasValidationErrors =
                getHasValidationErrors(validationErrors);
              setErrors(validationErrors);
              if (hasValidationErrors) {
                return;
              }
              if (userActionPermissions?.inviteOthers) {
                createUserMutation({
                  variables: {
                    input: userInput,
                  },
                });
              }
            }}
          >
            Invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InviteAdminOrManagerDialog;

gql`
  mutation CreateUser($input: UserInput!) {
    createUser(input: $input) {
      success
      error {
        code
        message
      }
      user {
        id
      }
    }
  }
`;
